import { useEffect, useState } from 'react';
import { Box, Text } from '@chakra-ui/react';
import { PublicToggle } from '@/pages/Organizations/components/shared/PublicToggle';
import { useUser } from '@/constants/UserContext';
import { supabase } from '@/lib/supabase';
import { logger } from '@/utils/logger';

/**
 * The account-wide chat notification switch.
 *
 * Saves on the click rather than through the page's Save bar. A switch that needs
 * confirming reads as broken, and this is the one control on the page someone reaches
 * for when they are already annoyed by notifications -- making them hunt for Save is the
 * wrong moment to be pedantic. The rest of the form keeps its dirty/Save flow untouched.
 *
 * Per-conversation mutes live on each chat's own menu; this is the blanket off switch.
 */
export function NotificationsCard() {
  const { user } = useUser();
  const userId = user?.id ?? null;

  const [enabled, setEnabled] = useState(true);
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    if (!userId) return;

    void supabase
      .from('profiles')
      .select('chat_notifications_enabled')
      .eq('id', userId)
      .maybeSingle()
      .then(({ data, error: loadError }) => {
        if (cancelled) return;
        if (loadError) {
          logger.error('NotificationsCard load failed', loadError);
        }
        setEnabled((data as { chat_notifications_enabled?: boolean } | null)?.chat_notifications_enabled ?? true);
        setLoaded(true);
      });

    return () => {
      cancelled = true;
    };
  }, [userId]);

  const handleToggle = async (next: boolean) => {
    if (!userId) return;
    const previous = enabled;
    setEnabled(next);
    setError(null);

    const { error: saveError } = await supabase
      .from('profiles')
      .update({ chat_notifications_enabled: next })
      .eq('id', userId);

    if (saveError) {
      logger.error('NotificationsCard save failed', saveError);
      setEnabled(previous);
      setError('Could not save that. Check your connection and try again.');
    }
  };

  return (
    <Box bg="white" borderRadius="2xl" p="6" boxShadow="0 8px 24px rgba(0,0,0,0.12)">
      <Text as="h3" fontSize="lg" fontWeight="semibold" color="volyx.primary" mb="4">
        Notifications
      </Text>

      <PublicToggle
        isPublic={enabled}
        onToggle={(next) => void handleToggle(next)}
        disabled={!loaded}
        title="Chat messages"
        publicLabel="On"
        privateLabel="Off"
        helperWhenPublic="You'll be notified about new messages in your chats"
        helperWhenPrivate="No chat notifications at all. Unread counts still show in Messages."
      />

      {error ? (
        <Text fontSize="sm" color="red.500" mt="2">
          {error}
        </Text>
      ) : null}
    </Box>
  );
}
